/**
 * TODO - preamble
 */
import { getNewVerificationRequestUrl, getProgramThemeUrl } from './ApiUrls';
import { getRouteOverride, getOverriddenMock } from './TestingRouteOverrides';
import { PostJson, PostFiles, GetJson, DeleteJson } from './Network';
import { fetchExistingVerificationRequest } from './VerificationApiClient/fetchExistingVerificationRequest';

import { logger } from '../utils/logger/logger';

import {
    DatabaseId,
    DocUploadResponse,
    ErrorResponse,
    NewVerificationRequest,
    StudentPersonalInfoViewModel,
    StudentPersonalInfoRequest,
    StudentPersonalInfoResponse,
    ViewModel,
    VerificationResponse,
    VerificationStep,
    MockStep,
    ProgramTheme,
    DocUploadViewModel,
    DocUploadRequest,
    SeniorPersonalInfoResponse,
    SeniorPersonalInfoViewModel,
    SeniorPersonalInfoRequest,
    TeacherPersonalInfoRequest,
    TeacherPersonalInfoViewModel,
    TeacherPersonalInfoResponse,
    MilitaryStatusResponse,
    MilitaryStatusRequest,
    ActiveMilitaryPersonalInfoResponse,
    ActiveMilitaryPersonalInfoViewModel,
    ActiveMilitaryPersonalInfoRequest,
    InactiveMilitaryPersonalInfoViewModel,
    InactiveMilitaryPersonalInfoRequest,
    InactiveMilitaryPersonalInfoResponse,
    SSOResponse,
    FirstResponderPersonalInfoRequest,
    FirstResponderPersonalInfoResponse,
    FirstResponderPersonalInfoViewModel,
    MedicalProfessionalPersonalInfoViewModel,
    MedicalProfessionalPersonalInfoResponse,
    MedicalProfessionalPersonalInfoRequest,
    EmploymentPersonalInfoViewModel,
    EmploymentPersonalInfoRequest,
    EmploymentPersonalInfoResponse,
} from '../types/types';

import { VerificationStepsEnum } from '../types/runtimeTypes';
import { assertValidVerificationStepName, assertValidTrackingId } from '../types/assertions';
import { getVerificationIdFromQueryString } from '../utils/routing/Url';
import { getOptions } from '../../options/options';
import { GetEmptyTheme } from '../types/empties';
import { DEFAULT_LOCALE } from '../../constants';
import { resolveTrackingId } from '../conversion/conversion';

/**
 * Initiate a new verification attempt with the SheerID REST service.
 *
 * @param programId Your programId from my.sheerid.com
 * @param trackingId Conversion trackingId to associate with this verification attempt.
 */
async function fetchNewVerificationRequest(programId: DatabaseId, trackingId: string = undefined): Promise<VerificationResponse> {
    const resolvedTrackingId = resolveTrackingId(trackingId);

    try {
        let response;
        const windowQueryString = window.location.search;
        let verificationId = getOptions().verificationId;

        if (!verificationId) {
            verificationId = getVerificationIdFromQueryString(windowQueryString);
        }

        if (verificationId) {
            logger.info('fetchNewVerificationRequest: Calling for existing verification request');
            response = fetchExistingVerificationRequest(verificationId);
        }

        if (!verificationId || response.currentStep === VerificationStepsEnum.error) {
            const url: string = getNewVerificationRequestUrl();
            let requestBody: NewVerificationRequest;
            if (resolvedTrackingId) {
                logger.info(`fetchNewVerificationRequest: Calling for new verification request with trackingId ${resolvedTrackingId}`);
                requestBody = {
                    programId,
                    trackingId: resolvedTrackingId,
                };
            } else {
                logger.info('fetchNewVerificationRequest: Calling for new verification request without trackingId');
                requestBody = {
                    programId,
                };
            }
            response = await PostJson(url, requestBody) as VerificationResponse;
        }

        const overriddenStep: MockStep = getRouteOverride();
        const mockResponse: VerificationResponse = await getOverriddenMock(overriddenStep, response);

        if (mockResponse) {
            return mockResponse;
        }

        return response;
    } catch (e) {
        logger.error(e);
    }
}

async function fetchProgramTheme(programId: DatabaseId): Promise<ProgramTheme> {
    if (!getOptions().doFetchTheme) {
        return GetEmptyTheme();
    }
    logger.info('fetchProgramTheme: Calling back-end for program theme');

    try {
        let url: string;
        const locale = getOptions().locale;

        if (locale !== DEFAULT_LOCALE) {
            url = getProgramThemeUrl(programId, locale);
        } else {
            url = getProgramThemeUrl(programId);
        }
        return await GetJson(url) as ProgramTheme;
    } catch (e) {
        logger.error(e);
    }
}

// StudentPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const studentViewModelToRequest = (viewModel: StudentPersonalInfoViewModel): StudentPersonalInfoRequest => {
    return {
        firstName: viewModel.firstName,
        lastName: viewModel.lastName,
        birthDate: viewModel.birthDate,
        email: viewModel.email,
        phoneNumber: viewModel.phoneNumber,
        organization: viewModel.organization,
        deviceFingerprintHash: viewModel.deviceFingerprintHash,
        metadata: viewModel.metadata,
    };
};

// TeacherPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const teacherViewModelToRequest = (viewModel: TeacherPersonalInfoViewModel): TeacherPersonalInfoRequest => {
    return {
        firstName: viewModel.firstName,
        lastName: viewModel.lastName,
        postalCode: viewModel.postalCode,
        email: viewModel.email,
        phoneNumber: viewModel.phoneNumber,
        organization: viewModel.organization,
        deviceFingerprintHash: viewModel.deviceFingerprintHash,
        metadata: viewModel.metadata,
    };
};

const seniorViewModelToRequest = (viewModel: SeniorPersonalInfoViewModel): SeniorPersonalInfoRequest => viewModel;

// ActiveMilitaryPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const militaryStatusViewModelToRequest = (viewModel: ActiveMilitaryPersonalInfoViewModel): MilitaryStatusRequest => {
    return {
        status: viewModel.status,
        phoneNumber: viewModel.phoneNumber,
    };
};

// ActiveMilitaryPersonalInfoViewModel contains 'status' which we don't want in the request so we can't return the full viewModel
const activeMilitaryViewModelToRequest = (viewModel: ActiveMilitaryPersonalInfoViewModel): ActiveMilitaryPersonalInfoRequest => {
    return {
        firstName: viewModel.firstName,
        lastName: viewModel.lastName,
        birthDate: viewModel.birthDate,
        email: viewModel.email,
        phoneNumber: viewModel.phoneNumber,
        organization: viewModel.organization,
        deviceFingerprintHash: viewModel.deviceFingerprintHash,
        metadata: viewModel.metadata,
    };
};

// InactiveMilitaryPersonalInfoViewModel contains 'status' which we don't want in the request so we can't return the full viewModel
const inactiveMilitaryViewModelToRequest = (viewModel: InactiveMilitaryPersonalInfoViewModel): InactiveMilitaryPersonalInfoRequest => {
    return {
        firstName: viewModel.firstName,
        lastName: viewModel.lastName,
        birthDate: viewModel.birthDate,
        email: viewModel.email,
        phoneNumber: viewModel.phoneNumber,
        organization: viewModel.organization,
        dischargeDate: viewModel.dischargeDate,
        deviceFingerprintHash: viewModel.deviceFingerprintHash,
        metadata: viewModel.metadata,
    };
};

async function submitStepCollectStudentPersonalInfo(
    previousResponse: StudentPersonalInfoResponse,
    viewModel: StudentPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = studentViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCollectSeniorPersonalInfo(
    previousResponse: SeniorPersonalInfoResponse,
    viewModel: SeniorPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = seniorViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCollectTeacherPersonalInfo(
    previousResponse: TeacherPersonalInfoResponse,
    viewModel: TeacherPersonalInfoViewModel,
): Promise<VerificationResponse> {
    logger.info('submitStepCollectTeacherPersonalInfo: Calling back-end for new verification request');
    let response;

    const requestBody = teacherViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCollectMilitaryStatus(
    previousResponse: MilitaryStatusResponse,
    viewModel: ActiveMilitaryPersonalInfoViewModel,
): Promise<VerificationResponse> {
    logger.info('submitStepCollectMilitaryStatus: Calling back-end for new verification request');
    let response;

    const requestBody = militaryStatusViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCollectActiveMilitaryPersonalInfo(
    previousResponse: ActiveMilitaryPersonalInfoResponse,
    viewModel: ActiveMilitaryPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = activeMilitaryViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCollectInactiveMilitaryPersonalInfo(
    previousResponse: InactiveMilitaryPersonalInfoResponse,
    viewModel: InactiveMilitaryPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = inactiveMilitaryViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

const firstResponderViewModelToRequest = (viewModel: FirstResponderPersonalInfoViewModel): FirstResponderPersonalInfoRequest => viewModel;

async function submitStepCollectFirstResponderPersonalInfo(
    previousResponse: FirstResponderPersonalInfoResponse,
    viewModel: FirstResponderPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = firstResponderViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

const medicalProfessionalViewModelToRequest = (viewModel: MedicalProfessionalPersonalInfoViewModel)
    : MedicalProfessionalPersonalInfoRequest => viewModel;

async function submitStepCollectMedicalProfessionalPersonalInfo(
    previousResponse: MedicalProfessionalPersonalInfoResponse,
    viewModel: MedicalProfessionalPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = medicalProfessionalViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

const employmentViewModelToRequest = (viewModel: EmploymentPersonalInfoViewModel)
    : EmploymentPersonalInfoRequest => viewModel;

async function submitStepCollectEmploymentPersonalInfo(
    previousResponse: EmploymentPersonalInfoResponse,
    viewModel: EmploymentPersonalInfoViewModel,
): Promise<VerificationResponse> {
    let response;

    const requestBody = employmentViewModelToRequest(viewModel);
    try {
        response = await PostJson(previousResponse.submissionUrl, requestBody);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepDocUpload(
    previousResponse: DocUploadResponse,
    viewModel: DocUploadViewModel,
): Promise<VerificationResponse> {
    logger.info('submitStepDocUpload', viewModel);
    let response;
    const requestBody: DocUploadRequest = viewModel;
    try {
        response = await PostFiles(previousResponse.submissionUrl, [requestBody.file1, requestBody.file2, requestBody.file3]);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

async function submitStepCancelSSO(
    previousResponse: SSOResponse,
): Promise<VerificationResponse> {
    logger.info('submitStepCancelSSO');
    let response;

    try {
        response = await DeleteJson(previousResponse.cancelUrl);
        return response;
    } catch (e) {
        logger.error(e);
        // At least show the error step...
        return getUnrecoverableErrorStep();
    }
}

function getUnrecoverableErrorStep(): ErrorResponse {
    // TODO build an empty error step response with "unknown error" or some other appropriate error id.
    const response: ErrorResponse = {
        verificationId: '',
        currentStep: 'error',
        segment: null,
        subSegment: null,
        errorIds: ['unknownError'],
    };
    return response;
}

async function submitStep(
    stepName: VerificationStep,
    previousResponse: VerificationResponse,
    viewModel: ViewModel,
): Promise<VerificationResponse> {
    assertValidVerificationStepName(stepName);
    switch (stepName) {
        case VerificationStepsEnum.collectStudentPersonalInfo:
            return submitStepCollectStudentPersonalInfo(
                previousResponse as StudentPersonalInfoResponse,
                viewModel as StudentPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectSeniorPersonalInfo:
            return submitStepCollectSeniorPersonalInfo(
                previousResponse as SeniorPersonalInfoResponse,
                viewModel as SeniorPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectTeacherPersonalInfo:
            return submitStepCollectTeacherPersonalInfo(
                previousResponse as TeacherPersonalInfoResponse,
                viewModel as TeacherPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectMilitaryStatus:
            return submitStepCollectMilitaryStatus(
                previousResponse as MilitaryStatusResponse,
                viewModel as ActiveMilitaryPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectActiveMilitaryPersonalInfo:
            return submitStepCollectActiveMilitaryPersonalInfo(
                previousResponse as ActiveMilitaryPersonalInfoResponse,
                viewModel as ActiveMilitaryPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectInactiveMilitaryPersonalInfo:
            return submitStepCollectInactiveMilitaryPersonalInfo(
                previousResponse as InactiveMilitaryPersonalInfoResponse,
                viewModel as InactiveMilitaryPersonalInfoViewModel,
            );
        case VerificationStepsEnum.sso:
            return submitStepCancelSSO(
                previousResponse as SSOResponse,
            );
        case VerificationStepsEnum.collectFirstResponderPersonalInfo:
            return submitStepCollectFirstResponderPersonalInfo(
                previousResponse as FirstResponderPersonalInfoResponse,
                viewModel as FirstResponderPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectMedicalProfessionalPersonalInfo:
            return submitStepCollectMedicalProfessionalPersonalInfo(
                previousResponse as MedicalProfessionalPersonalInfoResponse,
                viewModel as MedicalProfessionalPersonalInfoViewModel,
            );
        case VerificationStepsEnum.collectEmployeePersonalInfo:
            return submitStepCollectEmploymentPersonalInfo(
                previousResponse as EmploymentPersonalInfoResponse,
                viewModel as EmploymentPersonalInfoViewModel,
            );
        case VerificationStepsEnum.docUpload:
            return submitStepDocUpload(
                previousResponse as DocUploadResponse,
                viewModel as DocUploadViewModel,
            );
        default:
            Promise.reject(`Unknown step ${stepName}`);
    }
}

export const VerificationApiClient = {
    fetchNewVerificationRequest,
    fetchExistingVerificationRequest,
    fetchProgramTheme,
    submitStepCollectStudentPersonalInfo,
    submitStepDocUpload,
    submitStep,
};
